import {css, StyleSheet} from 'aphrodite';
import moment from 'moment';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {
  RemainingExecutionsAndTenantNameQuery,
  RemainingExecutionsAndTenantNameQueryVariables,
} from 'molbio/aa-sequences/alphafold/__generated/RemainingExecutionsAndTenantNameQuery';
import {REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY} from 'molbio/aa-sequences/alphafold/AlphaFoldTabUtils';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY_DARKER} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export function getTooltipText(tenantName: string, quota: number): string {
  const date = moment.tz('America/Los_Angeles').add(1, 'month').startOf('month');

  const tenantQualifier = BenchlingConfig.MOLBIO_ALPHAFOLD_IS_ACADEMIC ? '' : ` across ${tenantName}`;
  return `You have a total of ${quota} requests per month${tenantQualifier}. Requests will reset back to ${quota} on ${date.format(
    'LT z'
  )} at the start of every calendar month.`;
}

export const useGetRemainingExecutionsAndTenantName = (): {
  remainingExecutions: number | null;
  executionLimit: number | null;
  tenantName: string;
} => {
  const queryResult = useBenchlingQuery<
    RemainingExecutionsAndTenantNameQuery,
    RemainingExecutionsAndTenantNameQueryVariables
  >(REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY, {
    variables: {},
  });
  return queryResult.type === QueryResultType.SUCCESS
    ? {
        remainingExecutions: queryResult.data.remainingAlphafoldExecutions,
        executionLimit: queryResult.data.modelHubExecutionLimit,
        tenantName: queryResult.data.currentTenant.displayName,
      }
    : {remainingExecutions: null, executionLimit: null, tenantName: ''};
};

const ModelHubExecutionCountNotice = ({inBanner = true}: {inBanner?: boolean}): JSX.Element => {
  const {remainingExecutions, executionLimit, tenantName} = useGetRemainingExecutionsAndTenantName();
  const quota = executionLimit ?? BenchlingConfig.MOLBIO_ALPHAFOLD_EXECUTION_COUNT_LIMIT;

  if (!BenchlingConfig.MOLBIO_ALPHAFOLD_IS_ACADEMIC) {
    return <></>;
  }
  if (remainingExecutions == null) {
    return <></>;
  }
  if (inBanner) {
    return (
      <Tooltip text={getTooltipText(tenantName, quota)}>
        <InlineBanner
          text={`${remainingExecutions} of ${quota} requests remaining`}
          bannerType={remainingExecutions <= 0 ? InlineBannerType.WARNING : InlineBannerType.INFO}
        />
      </Tooltip>
    );
  } else {
    return (
      <div className={css(styles.optionButtonsLayout)}>
        <div>
          <Tooltip text={getTooltipText(tenantName, quota)}>
            <BenchlingIcon icon="info-circle" className={css(styles.infoIcon)} />
          </Tooltip>
          <span className={css(styles.infoText)}>
            {remainingExecutions} of {quota} requests remaining
          </span>
        </div>
      </div>
    );
  }
};

export default ModelHubExecutionCountNotice;

const styles = StyleSheet.create({
  infoIcon: {
    color: GRAY_DARKER,
  },
  infoText: {
    ...FONT_PROXIMA_13,
    marginLeft: '8px',
  },
  optionButtonsLayout: {
    padding: '0px 7px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '35px',
  },
});
